/**
 * Converts a Ukrainian first name to vocative case (кличний відмінок).
 *
 * Covers common patterns:
 *   - Владислав → Владиславе    (hard consonant ending)
 *   - Олександр → Олександре
 *   - Іван → Іване
 *   - Андрій → Андрію           (й ending)
 *   - Сергій → Сергію
 *   - Микола → Миколо           (-а → -о)
 *   - Ілля → Ілле               (-я → -е)
 *   - Олена → Олено             (female -а → -о)
 *   - Марія → Маріє             (-ія → -іє)
 *
 * If the name doesn't match a known pattern, returns it unchanged.
 */
export function toVocative(name: string): string {
  if (!name) return name;
  const n = name.trim();
  if (n.length < 2) return n;

  const last = n.slice(-1);
  const last2 = n.slice(-2);

  // -ія → -іє (Марія → Маріє, Юлія → Юліє)
  if (last2 === 'ія') return n.slice(0, -1) + 'є';

  // -я → -е (Ілля → Ілле)
  if (last === 'я') return n.slice(0, -1) + 'е';

  // -а → -о (Микола → Миколо, Олена → Олено)
  if (last === 'а') return n.slice(0, -1) + 'о';

  // -й → -ю (Андрій → Андрію, Сергій → Сергію)
  if (last === 'й') return n.slice(0, -1) + 'ю';

  // -ь → -ю (Василь → Василю, Ігор-ь... rare)
  if (last === 'ь') return n.slice(0, -1) + 'ю';

  // -о → unchanged (Дмитро, Петро — though formally має бути Дмитре/Петре,
  // в живій мові Дмитро/Петро лишають)
  if (last === 'о') return n;

  // hard consonant ending → +е (Владислав → Владиславе, Іван → Іване)
  const consonants = 'бвгґджзйклмнпрстфхцчшщ';
  if (consonants.includes(last.toLowerCase())) return n + 'е';

  return n;
}
